// src/pages/FileVaultPage.jsx
// File Vault -> Chat early Document Intelligence router handoff: 2026.09.10-v3
import React from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { fileVaultApi as fvApi } from "../utils/fileVaultApiClient"; // ⬅️ used for viewer URLs + File Vault Analyze

// Code-split the heavy FileVault UI for faster initial loads
const FileVault = React.lazy(() => import("../components/FileVault"));

const ACTIVE_CHAT_STORAGE_KEY = "active_chat";
const SUPPRESS_RESTORE_KEY = "suppress_chat_restore_until";
const DI_THREAD_SELECTION_STORAGE_PREFIX = "asklawly:document-intelligence:selected";

// Must match ChatInterface.jsx. File Vault writes exactly one short-lived
// early-handoff record after /vault/analyze has authenticated the user,
// verified billing/session access, and copied the original into the canonical
// Document Intelligence namespace. ChatInterface then owns /commit + /status.
const FILE_VAULT_DI_HANDOFF_STORAGE_KEY =
  "asklawly:file-vault:document-intelligence:handoff";
const FILE_VAULT_DI_HANDOFF_NAV_STATE_KEY =
  "fileVaultDocumentIntelligenceHandoff";
const FILE_VAULT_DI_HANDOFF_SCHEMA_VERSION = 1;

const normalizeCode = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const analyzeErrorCode = (error) => {
  const payload = error?.payload && typeof error.payload === "object" ? error.payload : {};
  const access =
    payload?.documentIntelligenceAccess &&
    typeof payload.documentIntelligenceAccess === "object"
      ? payload.documentIntelligenceAccess
      : payload?.document_intelligence_access &&
        typeof payload.document_intelligence_access === "object"
      ? payload.document_intelligence_access
      : {};

  return normalizeCode(
    error?.code ||
      payload?.code ||
      payload?.rejection_code ||
      payload?.failure_code ||
      access?.reason ||
      ""
  );
};

const analyzeDialogForError = (error) => {
  const code = analyzeErrorCode(error);

  if (code === "free_lifetime_document_intelligence_exhausted") {
    return {
      title: "Document Intelligence limit reached",
      message:
        "You’ve used both of your Free Document Intelligence analyses. Upgrade your plan to analyze additional documents.",
      billing: true,
      actionLabel: "View plans",
      actionPath: "/billing",
    };
  }

  if (code === "no_remaining_questions") {
    return {
      title: "Chat limit reached",
      message:
        "You’ve used all 30 Free Chat questions. Upgrade your plan to continue using Chat.",
      billing: true,
      actionLabel: "View plans",
      actionPath: "/billing",
    };
  }

  if (code === "trial_expired") {
    return {
      title: "Trial ended",
      message:
        "Your trial has ended. Choose a paid plan to continue using Document Intelligence.",
      billing: true,
      actionLabel: "View plans",
      actionPath: "/billing",
    };
  }

  if (code === "monthly_plan_expired") {
    return {
      title: "Plan expired",
      message:
        "Your monthly plan has expired. Renew your plan to continue using Document Intelligence.",
      billing: true,
      actionLabel: "View plans",
      actionPath: "/billing",
    };
  }

  if (
    code === "inactive_or_unsupported_plan" ||
    code === "document_intelligence_not_available_for_plan"
  ) {
    return {
      title: "Document Intelligence unavailable",
      message:
        "Document Intelligence is not included with your current plan. Choose an eligible plan to continue.",
      billing: true,
      actionLabel: "View plans",
      actionPath: "/billing",
    };
  }

  if (code === "billing_document_not_found") {
    return {
      title: "Billing account needs review",
      message:
        "We couldn’t verify Document Intelligence access for this account. Please review your plan or contact AskLawly support.",
      billing: true,
      actionLabel: "View plans",
      actionPath: "/billing",
    };
  }

  // Worker-side monthly chargeability rejection can use a more specific code
  // than Main's preflight. Keep this family match narrow and customer-facing.
  const monthlyDiAllowanceUsed =
    code.includes("monthly") &&
    (code.includes("document_intelligence") ||
      code.includes("analysis") ||
      code.includes("allowance")) &&
    (code.includes("exhaust") ||
      code.includes("allowance") ||
      code.includes("remaining") ||
      code.includes("unavailable"));

  if (monthlyDiAllowanceUsed) {
    return {
      title: "Document Intelligence allowance used",
      message:
        "Your current billing-period Document Intelligence allowance is used. Documents up to 5 pages remain included; longer documents require an available analysis. Upgrade your plan or wait for your allowance to reset.",
      billing: true,
      actionLabel: "View plans",
      actionPath: "/billing",
    };
  }

  return {
    title: "Couldn’t start analysis",
    message:
      "We couldn’t start Document Intelligence for this file. Please try again.",
    billing: false,
    actionLabel: "",
    actionPath: "",
  };
};

function FileVaultAnalyzeDialog({ dialog, onClose, onAction }) {
  if (!dialog?.open) return null;

  const busy = !!dialog.busy;

  return (
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (!busy && event.target === event.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        background: "rgba(15, 23, 42, 0.48)",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        boxSizing: "border-box",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="file-vault-analyze-dialog-title"
        aria-describedby="file-vault-analyze-dialog-message"
        style={{
          width: "min(460px, 100%)",
          background: "#fff",
          border: "1px solid rgba(15, 23, 42, 0.10)",
          borderRadius: 18,
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
          padding: "24px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div
            aria-hidden="true"
            style={{
              width: 42,
              height: 42,
              flex: "0 0 42px",
              borderRadius: 13,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: busy ? "#EEF2FF" : dialog.billing ? "#FFF7ED" : "#F1F5F9",
              color: busy ? "#1E2A78" : dialog.billing ? "#C2410C" : "#334155",
              fontSize: 20,
              fontWeight: 900,
            }}
          >
            {busy ? (
              <span
                style={{
                  width: 18,
                  height: 18,
                  border: "2px solid rgba(30, 42, 120, 0.22)",
                  borderTopColor: "#1E2A78",
                  borderRadius: "50%",
                  animation: "fileVaultAnalyzeSpin 0.8s linear infinite",
                }}
              />
            ) : dialog.billing ? (
              "!"
            ) : (
              "i"
            )}
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              id="file-vault-analyze-dialog-title"
              style={{
                fontSize: 18,
                lineHeight: 1.3,
                fontWeight: 850,
                color: "#0F172A",
                marginBottom: 8,
              }}
            >
              {dialog.title || "Document Intelligence"}
            </div>
            <div
              id="file-vault-analyze-dialog-message"
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: "#475569",
              }}
            >
              {dialog.message || ""}
            </div>
          </div>
        </div>

        {!busy ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 22,
              flexWrap: "wrap",
            }}
          >
            {dialog.actionLabel ? (
              <button
                type="button"
                onClick={onAction}
                style={{
                  border: "1px solid #1E2A78",
                  background: "#1E2A78",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 15px",
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {dialog.actionLabel}
              </button>
            ) : null}

            <button
              type="button"
              onClick={onClose}
              autoFocus
              style={{
                border: "1px solid #CBD5E1",
                background: "#fff",
                color: "#0F172A",
                borderRadius: 10,
                padding: "10px 15px",
                fontSize: 14,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              OK
            </button>
          </div>
        ) : null}
      </div>

      <style>{`
        @keyframes fileVaultAnalyzeSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default function FileVaultPage({
  setActiveChat,
  setActiveVision,
  onFirstMessageSent,
}) {
  const [ready, setReady] = React.useState(false);
  const [authed, setAuthed] = React.useState(false);
  const [analyzingNodeId, setAnalyzingNodeId] = React.useState("");
  const [analyzeDialog, setAnalyzeDialog] = React.useState({
    open: false,
    busy: false,
    billing: false,
    title: "",
    message: "",
    actionLabel: "",
    actionPath: "",
  });

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tab = (searchParams.get("tab") || "").toLowerCase() === "shared" ? "shared" : "my";

  React.useEffect(() => {
    document.title = tab === "shared" ? "File Vault — Shared with me" : "File Vault";
  }, [tab]);

  React.useEffect(() => {
    const auth = getAuth();
    const off = onAuthStateChanged(auth, (u) => {
      setAuthed(!!u);
      setReady(true);
    });
    return () => off();
  }, []);

  React.useEffect(() => {
    if (!analyzeDialog.open || analyzeDialog.busy) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setAnalyzeDialog((prev) => ({ ...prev, open: false }));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [analyzeDialog.open, analyzeDialog.busy]);

  const closeAnalyzeDialog = React.useCallback(() => {
    setAnalyzeDialog((prev) => {
      if (prev.busy) return prev;
      return { ...prev, open: false };
    });
  }, []);

  const handleAnalyzeDialogAction = React.useCallback(() => {
    const path = analyzeDialog.actionPath || "";
    setAnalyzeDialog((prev) => ({ ...prev, open: false }));
    if (path) navigate(path);
  }, [analyzeDialog.actionPath, navigate]);

  // ---------- helpers to build viewer URL ----------
  const isOfficeLike = (u = "") => /\.(docx?|xlsx?|pptx?|rtf)(\?|$)/i.test(String(u || ""));
  const isDocLike = isOfficeLike; // alias used below
  const isPdfLike = (u = "") => {
    const s = String(u || "");
    return /\.pdf($|\?)/i.test(s) || /contentType=application\/pdf/i.test(s);
  };
  const looksInlinePdf = (u = "") => {
    const s = String(u || "");
    const isPdf = isPdfLike(s);
    const forcedDownload = /response-content-disposition=attachment/i.test(s) || /download=1/i.test(s);
    return isPdf && !forcedDownload;
  };
  const looksInlineImage = (u = "") => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(String(u || ""));
  const safeTitle = (x) => (x || "Document").toString().trim() || "Document";
  const toIsoLike = (input) => {
    if (!input) return "";
    if (typeof input === "string") return input;
    if (typeof input === "number") {
      const ms = input > 1e12 ? input : input * 1000; // sec vs ms
      return new Date(ms).toISOString();
    }
    if (typeof input === "object" && input.seconds != null) {
      return new Date((input.seconds || 0) * 1000).toISOString();
    }
    try { return new Date(input).toISOString(); } catch { return ""; }
  };
  const ensurePdfZoom100 = (u) => {
    if (!u || !isPdfLike(u)) return u;
    const s = String(u);
    const hashIndex = s.indexOf("#");
    if (hashIndex === -1) return `${s}#zoom=100`;
    const before = s.slice(0, hashIndex);
    const hash = s.slice(hashIndex + 1);
    if (/(^|&)(zoom=)/i.test(hash)) {
      return `${before}#${hash.replace(/zoom=\d+/i, "zoom=100")}`;
    }
    return `${before}#${hash}${hash ? "&" : ""}zoom=100`;
  };

  // Accepts either a raw item OR a payload like { node, nodeId, title, owner }
  const openInViewer = async (itemOrPayload = {}) => {
    const src = itemOrPayload.node ? itemOrPayload.node : itemOrPayload;

    const nodeId = itemOrPayload.nodeId || src.id || "";
    const owner  = itemOrPayload.owner  || src.owner || src.ownerEmail || src.ownerId || "";

    const title = safeTitle(
      itemOrPayload.title ||
      src.name ||
      src.title ||
      src.filename
    );

    const createdAt = toIsoLike(
      src.createdAt || src.updatedAt || src.modifiedAt
    );

    // Common places these URLs might be on list items
    let previewUrl =
      src.previewUrl || src.viewerUrl || src.preview?.url || src.urls?.viewer || "";
    let downloadUrl =
      src.downloadUrl || src.originalUrl || src.urls?.download || src.url || "";

    // ROBUST: Always fetch a fresh signed download URL when we have a nodeId.
    // This avoids stale/incorrect URLs (a common cause of "No such key" at view time).
    const needSignedDownload = !!nodeId;

    if (needSignedDownload && nodeId) {
      try {
        const resp = await fvApi.getDownloadUrl({ nodeId, owner });
        const fetched = resp?.url || resp?.data?.url || "";
        if (fetched) downloadUrl = fetched;
      } catch {
        // non-fatal; viewer can still try previewUrl or resolver fallback
      }
    }

    // IMPORTANT: compute this AFTER fetching the latest downloadUrl
    const docxUrlCandidate = src.docxUrl || downloadUrl;

    const qp = new URLSearchParams();
    qp.set("title", title);
    if (createdAt) qp.set("createdAt", createdAt);

    // HTML bodies (rare for generic files; only if your list actually attaches them)
    if (src.html) qp.set("html", src.html);
    if (src.htmlDoc) qp.set("htmlDoc", src.htmlDoc);

    // Prefer iframe-friendly URL, but avoid stale previewUrl when a nodeId exists.
    // First try the fresh downloadUrl, then fall back to previewUrl if needed.
    if (!src.html && !src.htmlDoc) {
      if (downloadUrl && (looksInlinePdf(downloadUrl) || looksInlineImage(downloadUrl))) {
        qp.set("viewerUrl", ensurePdfZoom100(downloadUrl));
      } else if (previewUrl && (looksInlinePdf(previewUrl) || looksInlineImage(previewUrl))) {
        qp.set("viewerUrl", ensurePdfZoom100(previewUrl));
      }
    }

    // Always include a download URL if we have one (viewer uses it for universal preview & "Download")
    if (downloadUrl) qp.set("downloadUrl", downloadUrl);

    // If it’s an Office doc, also pass docxUrl so the viewer can use Office embed
    if (docxUrlCandidate && isDocLike(docxUrlCandidate)) {
      qp.set("docxUrl", docxUrlCandidate);
    }

    // Also include nodeId/kind so the viewer can resolve server-side if needed (harmless extra)
    if (nodeId) {
      qp.set("nodeId", nodeId);
      qp.set("kind", src.type === "folder" ? "folder" : "file");
    }

    navigate(`/file-viewer?${qp.toString()}`);
  };
  // -------------------------------------------------

  // File Vault Analyze bridge:
  // File bytes never return to the browser. /vault/analyze performs only the
  // secure File Vault preflight/session resolution and server-side copy into the
  // canonical Document Intelligence source namespace. Once Main returns the
  // accepted handoff (sessionId + documentId + turnId), this page immediately
  // navigates to ChatInterface. ChatInterface then uses the SAME normal Chat
  // attachment continuation: /chat/attachment/commit + authoritative /status.
  const analyzeInChat = React.useCallback(
    async (itemOrPayload = {}) => {
      const src = itemOrPayload.node ? itemOrPayload.node : itemOrPayload;
      if (!src || src.type === "folder") return;

      const nodeId = String(itemOrPayload.nodeId || src.id || "").trim();
      const owner = String(
        itemOrPayload.owner ||
          src.owner ||
          src.ownerEmail ||
          src.ownerId ||
          ""
      ).trim();
      const fileName = safeTitle(
        itemOrPayload.title || src.name || src.title || src.filename
      );

      if (!nodeId) {
        setAnalyzeDialog({
          open: true,
          busy: false,
          billing: false,
          title: "Couldn’t start analysis",
          message: "This file is missing the information needed to start Document Intelligence.",
          actionLabel: "",
          actionPath: "",
        });
        return;
      }

      if (analyzingNodeId) return;

      // A brand-new File Vault Analyze replaces any stale unconsumed browser
      // handoff record. A committed/processing DI document remains recoverable
      // through ChatInterface's authoritative document/status recovery path.
      try {
        localStorage.removeItem(FILE_VAULT_DI_HANDOFF_STORAGE_KEY);
      } catch {}

      setAnalyzingNodeId(nodeId);
      setAnalyzeDialog({
        open: true,
        busy: true,
        billing: false,
        title: "Starting Document Intelligence",
        message: `Preparing “${fileName}” for analysis.`,
        actionLabel: "",
        actionPath: "",
      });

      try {
        const result = await fvApi.analyzeFile({ nodeId, owner });

        if (result?.success === false) {
          const syntheticError = new Error("Analyze request was not accepted.");
          syntheticError.code = result?.code || null;
          syntheticError.payload = result;
          throw syntheticError;
        }

        const chatSession =
          result?.chatSession && typeof result.chatSession === "object"
            ? result.chatSession
            : {};

        const sessionId = String(
          result?.sessionId ||
            result?.session_id ||
            chatSession?.sessionId ||
            chatSession?.session_id ||
            ""
        ).trim();

        const documentId = String(
          result?.documentId ||
            result?.document_id ||
            result?.document?.document_id ||
            result?.document?.documentId ||
            ""
        ).trim();

        const turnId = String(
          result?.turnId ||
            result?.turn_id ||
            result?.next?.body?.turnId ||
            result?.next?.body?.turn_id ||
            ""
        ).trim();

        const mime = String(
          result?.mime ||
            result?.mimeType ||
            result?.mime_type ||
            result?.fileVaultSource?.contentType ||
            result?.document?.mime_type ||
            src?.contentType ||
            ""
        ).trim();

        const sizeBytesRaw =
          result?.sizeBytes ??
          result?.size_bytes ??
          result?.document?.size_bytes ??
          src?.size ??
          null;

        const parsedSizeBytes = Number(sizeBytesRaw);
        const sizeBytes =
          Number.isFinite(parsedSizeBytes) && parsedSizeBytes > 0
            ? parsedSizeBytes
            : null;

        const commitRequired =
          result?.commitRequired !== false &&
          result?.commit_required !== false;

        if (!sessionId) {
          throw new Error("Document Intelligence started without a Chat session.");
        }

        if (!documentId) {
          throw new Error("Document Intelligence started without a document ID.");
        }

        if (!commitRequired) {
          throw new Error("Document Intelligence handoff was incomplete.");
        }

        const sessionObj = {
          session_id: sessionId,
          session_name: chatSession?.session_name || null,
          plan_type: chatSession?.plan_type || null,
          reused_existing_session: !!chatSession?.reused_existing_session,
        };

        /*
         * IMPORTANT HANDOFF BOUNDARY
         * --------------------------
         * /vault/analyze has already:
         *   - authenticated/authorized the File Vault source
         *   - passed the current DI soft-check
         *   - resolved/reused/created the correct Chat session
         *   - copied the original file into the canonical DI namespace
         *
         * It has NOT started the long worker dispatch. Persist one short-lived
         * handoff record before navigation. ChatInterface consumes it and enters
         * the same AttachmentController continuation used by a normal Chat
         * attachment: /commit + authoritative /status polling + the existing
         * attachment-only Lexa completion message.
         */
        const handoffRecord = {
          schemaVersion: FILE_VAULT_DI_HANDOFF_SCHEMA_VERSION,
          source: "file_vault",
          sessionId,
          documentId,
          turnId,
          fileName,
          mime,
          sizeBytes,
          commitRequired: true,
          createdAt: Date.now(),
          fileVaultSource:
            result?.fileVaultSource &&
            typeof result.fileVaultSource === "object"
              ? result.fileVaultSource
              : {
                  nodeId,
                  name: fileName,
                  contentType: mime || null,
                },
        };

        try {
          const authUser = getAuth().currentUser;
          const ownerKey = String(authUser?.uid || authUser?.email || "owner");

          // Persist a recovery copy FIRST. The live transition also carries this
          // exact handoff through React Router state below. ChatInterface accepts
          // either channel and starts the same long-running continuation.
          localStorage.setItem(
            FILE_VAULT_DI_HANDOFF_STORAGE_KEY,
            JSON.stringify(handoffRecord)
          );

          localStorage.setItem(
            ACTIVE_CHAT_STORAGE_KEY,
            JSON.stringify({
              session_id: sessionObj.session_id,
              session_name: sessionObj.session_name,
            })
          );
          localStorage.removeItem(SUPPRESS_RESTORE_KEY);
          localStorage.removeItem("chat_input");

          // Preselect the exact File Vault document so a reused Free session
          // cannot reopen an older previously selected DI document.
          localStorage.setItem(
            `${DI_THREAD_SELECTION_STORAGE_PREFIX}:${ownerKey}:${sessionId}`,
            documentId
          );
        } catch (storageError) {
          console.error(
            "[FileVaultPage] Unable to persist Document Intelligence handoff:",
            storageError
          );
          throw new Error(
            "Unable to transfer this analysis into Chat right now."
          );
        }

        try {
          setActiveVision?.(null);
          setActiveChat?.(sessionObj);
        } catch {}

        try {
          if (typeof onFirstMessageSent === "function") {
            onFirstMessageSent();
          } else {
            window.dispatchEvent(new Event("triggerRefreshSidebar"));
          }
        } catch {}

        // The File Vault waiting dialog ends HERE. From this point forward the
        // normal ChatInterface processing card + DI dock own the visible wait.
        //
        // Deliver the accepted handoff directly through React Router state.
        // localStorage above remains only the recovery copy if the route remount
        // or browser lifecycle interrupts this immediate transition.
        setAnalyzeDialog((prev) => ({ ...prev, open: false, busy: false }));
        navigate("/dashboard", {
          state: {
            [FILE_VAULT_DI_HANDOFF_NAV_STATE_KEY]: handoffRecord,
          },
        });
      } catch (error) {
        const nextDialog = analyzeDialogForError(error);
        setAnalyzeDialog({
          open: true,
          busy: false,
          ...nextDialog,
        });
      } finally {
        setAnalyzingNodeId("");
      }
    },
    [
      analyzingNodeId,
      navigate,
      onFirstMessageSent,
      setActiveChat,
      setActiveVision,
    ]
  );

  const wrap = { maxWidth: 960, margin: "24px auto", padding: "0 16px 40px" };
  const card = {
    padding: 24,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    textAlign: "center",
  };
  const titleCss = { fontSize: 18, fontWeight: 800, marginBottom: 6 };
  const text = { fontSize: 14, color: "#6b7280" };
  const cta = {
    display: "inline-block",
    marginTop: 12,
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #111827",
    background: "#111827",
    color: "#fff",
    fontWeight: 800,
    textDecoration: "none",
  };

  if (!ready) {
    return (
      <div style={wrap}>
        <div style={{ ...card, textAlign: "left" }}>Loading…</div>
      </div>
    );
  }

  if (!authed) {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={titleCss}>Please log in</div>
          <div style={text}>Sign in to access your File Vault.</div>
          <div>
            <Link to="/login" style={cta}>Log in</Link>
          </div>
        </div>
      </div>
    );
  }

  // When authenticated, render FileVault with a consistent fallback
  // Pass defaultMode based on ?tab=shared and a key so mode changes remount the component.
  const modeKey = tab === "shared" ? "shared" : "my";

  return (
    <>
      <FileVaultAnalyzeDialog
        dialog={analyzeDialog}
        onClose={closeAnalyzeDialog}
        onAction={handleAnalyzeDialogAction}
      />

      <React.Suspense
        fallback={
          <div style={wrap}>
            <div style={{ ...card, textAlign: "left" }}>
              {tab === "shared" ? "Loading items shared with you…" : "Loading files…"}
            </div>
          </div>
        }
      >
        {/* Important: give the list callbacks for its Open + Analyze actions. */}
        <FileVault
          key={modeKey}
          defaultMode={modeKey}
          onOpen={openInViewer}
          onAnalyze={analyzeInChat}
          analyzingNodeId={analyzingNodeId}
        />
      </React.Suspense>
    </>
  );
}
